import React, { useState } from "react";
import { FollowUpCard } from "../../followup/cards/FollowUpCard";
import { OutsideReferralCard } from "../../followup/cards/OutsideReferralCard";
import { ReferProgramCard } from "../../followup/cards/ReferProgramCard";
import { InternalNotesCard } from "../../followup/cards/InternalNotesCard";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import { PrescriptionPreviewDialog } from "../../../dialogs/PrescriptionPreviewDialog";
import { PrescriptionViewDialog } from "../../../dialogs/PrescriptionViewDialog";
import {
  useMealStrategy,
  useNutritionTarget,
} from "@/hooks/useNutritionEvaluation";

interface NutritionFollowupTabProps {
  appointmentId: string;
  isCompleted?: boolean;
  prescriptionUrl?: string | null;
}

export const NutritionFollowupTab: React.FC<NutritionFollowupTabProps> = ({
  appointmentId,
  isCompleted = false,
  prescriptionUrl,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [showView, setShowView] = useState(false);

  const { data: target } = useNutritionTarget(appointmentId);
  const { data: mealStrategy } = useMealStrategy(appointmentId);

  const missingFields: { label: string; hint: string }[] = [];

  if (!mealStrategy?.strategies?.length) {
    missingFields.push({
      label: "Meal Strategy",
      hint: "Meal Plan tab → Meal Strategy",
    });
  }
  if (!target?.weight) {
    missingFields.push({
      label: "Target Weight",
      hint: "Targets tab → Weight / Energy Targets",
    });
  }
  if (!target?.protein) {
    missingFields.push({
      label: "Target Protein",
      hint: "Targets tab → Macronutrient Targets",
    });
  }
  if (!target?.carbs) {
    missingFields.push({
      label: "Target Carbohydrates",
      hint: "Targets tab → Macronutrient Targets",
    });
  }
  if (!target?.fat) {
    missingFields.push({
      label: "Target Fat",
      hint: "Targets tab → Macronutrient Targets",
    });
  }
  if (!target?.fibre) {
    missingFields.push({
      label: "Target Fibre",
      hint: "Targets tab → Macronutrient Targets",
    });
  }

  return (
    <div className="space-y-4">
      <FollowUpCard
        appointmentId={appointmentId}
        isCompleted={isCompleted}
        parentType="meal_plan"
      />
      <OutsideReferralCard
        appointmentId={appointmentId}
        isCompleted={isCompleted}
        parentType="meal_plan"
      />
      <ReferProgramCard
        appointmentId={appointmentId}
        isCompleted={isCompleted}
        parentType="meal_plan"
      />
      <InternalNotesCard
        appointmentId={appointmentId}
        isCompleted={isCompleted}
        parentType="meal_plan"
      />

      {/* Preview button for active appointments, View button for completed appointments */}
      <div className="flex justify-end pt-4">
        {isCompleted ? (
          <Button onClick={() => setShowView(true)} className="gap-2">
            <Eye className="h-4 w-4" />
            View Nutrition Report
          </Button>
        ) : (
          <Button onClick={() => setShowPreview(true)} className="gap-2">
            <Eye className="h-4 w-4" />
            Preview Nutrition Report
          </Button>
        )}
      </div>

      {/* Preview Dialog for active appointments */}
      <PrescriptionPreviewDialog
        open={showPreview}
        onOpenChange={setShowPreview}
        appointmentId={appointmentId}
        parentType="meal_plan"
        missingFields={missingFields}
      />

      {/* View Dialog for completed appointments */}
      <PrescriptionViewDialog
        open={showView}
        onOpenChange={setShowView}
        appointmentId={appointmentId}
        prescriptionUrl={prescriptionUrl}
        title="Nutrition Report"
        parentType="meal_plan"
      />
    </div>
  );
};
